"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { Card } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function ServiceAreaMap() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>();
  const mapReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="256903" id="serviceareamap" className="bg-background py-16 md:py-24 w-full">
      <div className="container mx-auto px-[var(--content-margin-x)] max-w-[var(--max-content-width)]">
        <div ref={headerReveal.ref} className={`text-center max-w-3xl mx-auto mb-12 transition-all duration-700 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl font-bold font-default text-secondary mb-4">
            Our Service Area
          </h2>
          <p className="text-lg text-foreground font-default">Chipper Mulching Co. proudly serves Burlington, NC, Alamance County, the entire Piedmont Triad region, and all of North Carolina. From forestry mulching to lot prep, we bring professional land clearing to your doorstep.</p>
        </div>

        <div ref={mapReveal.ref} className={`transition-all duration-1000 ease-out delay-200 ${mapReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Card className="overflow-hidden border-border shadow-xl p-0 rounded-lg">
            <AspectRatio ratio={16 / 9} className="bg-muted relative group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/piedmont_triad_service_map.png" alt="Map showing Chipper Mulching Co service area in Burlington and Piedmont Triad" className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/piedmont_triad_service_map.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/piedmont_triad_service_map.png-wvc-sizes" loading="lazy" />
              
              {}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="bg-background/95 backdrop-blur-md px-6 py-3 rounded-lg shadow-lg border border-primary/20 flex items-center gap-3 transform transition-transform duration-500 group-hover:-translate-y-2">
                  <div className="bg-primary/20 p-2 rounded-md">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex flex-col text-left">
                    <span className="font-bold font-default text-foreground leading-tight">Burlington, NC</span>
                    <span className="text-xs text-primary font-bold font-default uppercase tracking-wider">Piedmont Triad HQ</span>
                  </div>
                </div>
              </div>
            </AspectRatio>
          </Card>
        </div>
      </div>
    </section>;
}